import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import AppShell from "../components/AppShell";
import { useAuth } from "../contexts/AuthContext";
import { useHasPermission } from "../contexts/PermissionsContext";
import { api, formatApiError } from "../lib/api";
import { AlertTriangle, Info, AlertOctagon, ArrowRight, ClipboardCheck } from "lucide-react";
import { toast } from "sonner";

const fmtUSD = (n) => `$${Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function Dashboard() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const canReviewDiscipline = useHasPermission("discipline.review", null);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [pendingReviews, setPendingReviews] = useState(null);

    useEffect(() => {
        api.get("/dashboard/overview")
            .then(({ data }) => setData(data))
            .catch((e) => toast.error(formatApiError(e)))
            .finally(() => setLoading(false));
    }, []);

    // Load count of discipline cases awaiting HR/Admin review (only if the
    // user actually has that permission — mirrors the pinned panel on /discipline).
    useEffect(() => {
        if (!canReviewDiscipline) { setPendingReviews(null); return; }
        api.get("/discipline", { params: { status: "awaiting_review", page_size: 1 } })
            .then(({ data }) => setPendingReviews(data.total ?? (data.items || []).length))
            .catch(() => setPendingReviews(0));
    }, [canReviewDiscipline]);

    // Pick a module code to navigate to when the KPI is clicked. Prefer the
    // user's scoped module code (dept mgrs), else the first module returned in
    // the overview, else fall back to the raw /discipline path (router will
    // 404 gracefully if the module list is empty).
    const disciplineModuleCode = user?.module_scope_code || (data?.workforce?.by_module?.[0]?.code) || "AP";
    const disciplineHref = `/m/${disciplineModuleCode}/discipline?status=awaiting_review`;

    if (loading || !data) return <AppShell title="Dashboard"><div className="asl-card p-6 text-sm">Loading…</div></AppShell>;

    const wf = data.workforce; const at = data.attendance_today;
    const py = data.payroll; const fi = data.finance; const co = data.contracts;

    return (
        <AppShell title="Dashboard">
            <section className="space-y-5" data-testid="dashboard-page">
                <div className="asl-card p-5">
                    <div className="text-xs uppercase tracking-wider text-[color:var(--asl-muted)]">Welcome</div>
                    <h2 className="text-2xl font-bold asl-navy-text">{user?.full_name || user?.email}</h2>
                    <div className="text-sm text-[color:var(--asl-muted)]">{user?.role.replace(/_/g, " ")}</div>
                </div>

                {/* Alerts */}
                {data.alerts?.length > 0 && (
                    <div className="space-y-2" data-testid="dash-alerts">
                        {data.alerts.map((a, i) => <AlertRow key={i} alert={a} onNavigate={navigate} />)}
                    </div>
                )}

                {/* Pending Discipline Reviews — Kaafi HR / Admin only */}
                {canReviewDiscipline && (
                    <div
                        className="asl-card p-4 border-l-4 cursor-pointer hover:shadow-md transition"
                        style={{ borderLeftColor: "var(--asl-crimson)" }}
                        onClick={() => navigate(disciplineHref)}
                        role="button"
                        data-testid="dash-pending-discipline-reviews"
                    >
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <ClipboardCheck className="h-5 w-5 asl-crimson-text" />
                                <div>
                                    <div className="text-[10px] uppercase tracking-wider text-[color:var(--asl-muted)]">
                                        Kaafi review queue
                                    </div>
                                    <div className="text-lg font-bold asl-navy-text">
                                        Pending discipline reviews
                                    </div>
                                </div>
                            </div>
                            <div className="text-3xl font-bold asl-crimson-text mono" data-testid="dash-pending-discipline-count">
                                {pendingReviews === null ? "…" : pendingReviews}
                            </div>
                        </div>
                    </div>
                )}

                {/* Workforce + Today */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="dash-workforce">
                    <Stat label="Active workforce" value={wf.total_active} link="/staff" navigate={navigate} accent />
                    <Stat label="Casual" value={wf.casual} link="/staff?employment_type=casual" navigate={navigate} />
                    <Stat label="Non-casual" value={wf.non_casual} link="/staff?employment_type=non_casual" navigate={navigate} />
                    <Stat label="Today: pending review" value={at.pending_review_events} link="/attendance/exceptions" navigate={navigate} testid="pending-review-card" />
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="dash-attendance">
                    <Stat label="Today present" value={at.present} link="/attendance?status=present" navigate={navigate} />
                    <Stat label="Today late" value={at.late} link="/attendance?status=late" navigate={navigate} />
                    <Stat label="Today absent" value={at.absent} link="/attendance?status=absent" navigate={navigate} />
                    <Stat label="Today exceptions" value={at.exception} link="/attendance?status=exception" navigate={navigate} />
                </div>

                {/* Payroll status */}
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3" data-testid="dash-payroll">
                    <Stat label="Payroll in progress" value={py.periods_in_progress} link="/payroll" navigate={navigate} />
                    <Stat label="Awaiting funding" value={py.periods_awaiting_funding} link="/payroll" navigate={navigate} accent={py.periods_awaiting_funding > 0} />
                    <Stat label="Closed this month" value={py.periods_closed_this_month} link="/payroll" navigate={navigate} />
                </div>

                {/* Finance row */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="dash-finance">
                    <Stat label="Net payable (this month)" value={fmtUSD(fi.net_payable_this_month)} link="/finance" navigate={navigate} accent />
                    <Stat label="Funding outstanding" value={fmtUSD(fi.funding_outstanding)} link="/payroll/funding" navigate={navigate} accent={fi.funding_outstanding > 0} />
                    <Stat label="Mgmt fee (this month)" value={fmtUSD(fi.management_fee_this_month)} link="/finance" navigate={navigate} />
                </div>

                {/* Modules */}
                {wf.by_module?.length > 0 && (
                    <div>
                        <h3 className="text-sm font-bold asl-navy-text mb-2 uppercase tracking-wide">By module</h3>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="dash-modules">
                            {wf.by_module.map((m) => (
                                <div key={m.module_id} className="asl-card p-4 cursor-pointer hover:shadow-md transition"
                                    onClick={() => navigate(`/staff?module_id=${m.module_id}`)}
                                    data-testid={`dash-mod-${m.code}`}>
                                    <div className="flex items-baseline justify-between">
                                        <h4 className="text-lg font-bold asl-navy-text">{m.name}</h4>
                                        <span className="text-xs asl-crimson-text">{m.code}</span>
                                    </div>
                                    <div className="text-3xl font-bold asl-navy-text mt-2 mono">{m.count}</div>
                                    <div className="text-xs text-[color:var(--asl-muted)]">active workers</div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Contracts */}
                <div className="grid grid-cols-2 gap-3" data-testid="dash-contracts">
                    <Stat label="Contracts expiring 30d" value={co.expiring_30d} link="/contracts" navigate={navigate} accent={co.expiring_30d > 0} />
                    <Stat label="Contracts past end date" value={co.expired} link="/contracts" navigate={navigate} accent={co.expired > 0} />
                </div>
            </section>
        </AppShell>
    );
}

function Stat({ label, value, link, navigate, accent, testid }) {
    return (
        <div
            className="asl-card p-4 cursor-pointer hover:shadow-md transition group"
            onClick={() => link && navigate(link)}
            data-testid={testid || `dash-stat-${String(label).toLowerCase().replace(/\s+/g, "-")}`}
        >
            <div className="text-[10px] uppercase tracking-wider text-[color:var(--asl-muted)]">{label}</div>
            <div className={`text-2xl font-bold mt-1 mono ${accent ? "asl-crimson-text" : "asl-navy-text"}`}>{value}</div>
            {link && <div className="text-[10px] text-[color:var(--asl-muted)] mt-1 group-hover:asl-navy-text">view <ArrowRight className="h-3 w-3 inline" /></div>}
        </div>
    );
}

function AlertRow({ alert, onNavigate }) {
    const Icon = alert.severity === "critical" ? AlertOctagon
        : alert.severity === "warn" ? AlertTriangle : Info;
    const tone = alert.severity === "critical"
        ? "border-rose-400 bg-rose-50 text-rose-900"
        : alert.severity === "warn"
            ? "border-amber-400 bg-amber-50 text-amber-900"
            : "border-blue-400 bg-blue-50 text-blue-900";
    return (
        <div
            className={`flex items-center gap-3 p-3 border-l-4 rounded-r ${tone} cursor-pointer hover:opacity-90`}
            onClick={() => alert.link && onNavigate(alert.link)}
            role={alert.link ? "link" : undefined}
            data-testid="dash-alert-item"
        >
            <Icon className="h-4 w-4 shrink-0" />
            <div className="flex-1 text-sm" data-testid={`dash-alert-${alert.severity}`}>{alert.message}</div>
            {alert.link && <ArrowRight className="h-4 w-4" />}
        </div>
    );
}
